
import { Component, Vue } from "vue-property-decorator";
import { inject } from "inversify-props";
import { ValidationErrors } from "fluentvalidation-ts/dist/ValidationErrors";
import { types, UserInfo, IOfflineInfo, SiteAppNonCongregateMeal } from "@/store";
import { routePaths } from "@/router";
import {
  MealCountDto,
  MealCountSoftValidator,
  MealCountHardValidator,
  MealCountCheck,
  MealCountCalcs,
  ReceivedMeals,
  ServingLine,
  SaveMealCountRequest,
  MealCountVersion,
  FeatureFlagInfo,
  MealCountKey,
  SiteAppInfo,
  MealType,
  ProgramType,
} from "@/models";
import { MealCountDtoViewModel } from "@/view-models";
import { IRouterService, SyncService } from "@/services";
import {
  ObjectHelper,
  mcDate,
  eventBus,
  mcEvents,
  clone,
  mcCounts,
  mcLogger,
  OfflineInfoHelper,
} from "@/utils";
import { Guid } from "guid-typescript";
import moment from "moment";
import _ from "lodash";

@Component
export default class McMealWizard extends Vue {
  @inject() private readonly routerService!: IRouterService;
  @inject() private readonly syncService!: SyncService;
  private routeInfo: any;
  key!: MealCountKey;
  mealKeys: MealCountKey[] = [];
  meals: MealCountDtoViewModel[] = []; // array of meal count view models

  /** The group id to use when joining or leaving a SignalR group. Based off of this.key */
  private signalRGroupId!: string;

  private pristine: MealCountDtoViewModel[] = [];

  saved = false;
  isDirty = false;
  defaultTimeStamp = 0;

  featureFlagInfo: FeatureFlagInfo = new FeatureFlagInfo();
  tempServingLineTrackingId = "";

  // Validations
  // receive, serve, and waste
  softValidations: ValidationErrors<MealCountDtoViewModel> = {};
  // tally and summary
  hardValidations: ValidationErrors<MealCountDto> = {};
  private readonly softValidator: MealCountSoftValidator =
    new MealCountSoftValidator();
  private readonly hardValidator: MealCountHardValidator =
    new MealCountHardValidator();

  selectedProvidedDays: number = 1;
  providedDaysOptions: number[] = [...Array(10).keys()].map(i => i + 1);
  multiplyMealCounts: boolean = false;

  created() {
    this.routeInfo = this.routerService.getParams();
    this.key = this.routerService.getMealCountKey()
    this.signalRGroupId = this.key.toString()

    this.key = this.routerService.getMealCountKey();
    this.mealKeys = this.getMealKeys(this.key);
    this.meals = this.mealKeys.map((key) => {
      return this.getNewModel(key);
    });

    this.featureFlagInfo = this.$store.getters[types.state.featureFlags].find(
      (m) => m.siteId == this.meals[0].mealCounts.siteId
    );
  }

  mounted() {
    // on a page refresh, this component is rendered before any new data is synced down
    // when this happens, the event listener will reload data
    eventBus.$on(mcEvents.syncedMealsDown, this.loadTempModel);

    this.loadTempModel();
    (this.$refs.wizard as any).activateAll();

    if (this.isMslMode) {
      // Listen for notifications that a count was updated
      eventBus.$on(mcEvents.receiveUpdatedCount, this.onReceiveUpdatedCount);

      // Get SignalR group id and join it
      eventBus.$emit(mcEvents.enteredCountingWorkflow, this.signalRGroupId);
    }
  }

  beforeDestroy() {
    this.$store.dispatch(types.actions.setTimeStamp, this.defaultTimeStamp);
    eventBus.$off(mcEvents.syncedMealsDown);

    if (this.isMslMode) {
      eventBus.$emit(mcEvents.exitedCountingWorkflow, this.signalRGroupId);
      eventBus.$off(mcEvents.receiveUpdatedCount);
    }
  }

  getMealKeys(key: MealCountKey) {
    let result: MealCountKey[] = [];

    // if non-congregate, show all meal types
    if (key.mealTypeId === "NON_CONGREGATE") {
      const mealTypes = OfflineInfoHelper.getNonCongregateMealTypes(key, this.$store.getters[types.state.siteAppDates]);
      result = mealTypes
        .filter((mealType) => mealType.toString() !== MealType.NonCongregate.toString())
        .map((mealType) => new MealCountKey(key.siteId, ProgramType[key.programId], mealType.toString(), key.mealServiceDate, new Date(key.syncDateUtc || "")));
    } else {
      result = [key];
    }
    return result;
  }

  getNewModel(key: MealCountKey) {
    const date = moment.utc().format();
    const user = this.$store.state.auth.user;

    console.log(this.routeInfo?.mealType === "NON_CONGREGATE")

    let result = new MealCountDtoViewModel(
      new MealCountDto(
        Guid.create().toString(),
        key.siteId,
        key.programId,
        key.mealTypeId,
        key.mealServiceDate,
        date,
        this.routeInfo?.mealType === "NON_CONGREGATE",
        this.selectedProvidedDays,
        new ReceivedMeals(
          "",
          0,
          0,
          0,
          0,
          user.userId,
          user.firstName,
          user.lastName,
          date,
          date
        ),
        [
          new ServingLine(
            Guid.create().toString(),
            0,
            0,
            0,
            0,
            0,
            0,
            0,
            0,
            0,
            false,
            user.userId,
            user.firstName,
            user.lastName,
            date,
            date
          ),
        ]
      )
    );
    return result;
  }

  get mealAffirmed() : boolean {
    const userServingLine = this.meals.filter((m) => m.affirmed === true)[0]?.userServingLine;
    return userServingLine ? userServingLine.affirmed : false;
  }

  set mealAffirmed(value: boolean) {
    for (const meal of this.meals) {
      const isNonCongregate = meal.mealCounts.isNonCongregate as boolean;
      let userServingLine = meal.userServingLine;

      if (!userServingLine) {
        continue;
      }

      if (isNonCongregate) {
        if (userServingLine.mealCountNonCongregateMeal > 0 || userServingLine.mealCountWaste > 0 || userServingLine.mealCountIncomplete > 0) {
          userServingLine.affirmed = value;
          userServingLine.modifiedDateTimeUtc = moment.utc().format();
          this.affirmChanged();
          this.$emit("affirmedValue", userServingLine.affirmed);
        } 
      } else {
        userServingLine.affirmed = value;
        userServingLine.modifiedDateTimeUtc = moment.utc().format();
        this.affirmChanged();
        this.$emit("affirmedValue", userServingLine.affirmed);
      }
      
    }
  }

  get isNonCongregate() {
    return !!this.meals[0]?.mealCounts.isNonCongregate as boolean;
  }

  countComparison() {
    let countCheck = false;
    for (const meal of this.meals) {
      const mealCountCalc = new MealCountCalcs(meal.mealCounts);
      countCheck = mealCountCalc.newLeftovers < mealCountCalc.availableCount;
      if (countCheck)
        break;
    }
    this.$emit("mealCountCalc", countCheck);
  }

  get isMslMode() {
    return (
      this.featureFlagInfo.msl.isActive ||
      this.meals[0].mealCounts.servingLines.length > 1
    );
  }

  /** Process the received SignalR message. */
  async onReceiveUpdatedCount(mealCountKey: MealCountKey) {
    await this.syncService.syncMealsDown([mealCountKey]);
  }

  /**
   * Update the local meal counts with the received info
   */
  loadTempModel() {
    this.pristine = [];
    const currentUser: UserInfo = this.$store.state.auth.user;

    for (const mealKey of this.mealKeys) {
      let existingMealCountsWithMatchingKey: MealCountDto =
        this.$store.state.meals.mealCountDtos.find((m) =>
          mcCounts.matchMealCountKey(m, mealKey)
        );
      const tempCounts: MealCountDto = this.$store.state.meals.tempDto.find((m) =>
        mcCounts.matchMealCountKey(m, mealKey)
      );

      this.updateTempDtoForOtherUsers(
        tempCounts,
        currentUser,
        existingMealCountsWithMatchingKey
      );

      if (existingMealCountsWithMatchingKey) {
        this.updateMealCountsWithExistingMealCounts(
          existingMealCountsWithMatchingKey,
          currentUser
        );
      } else {
        // No existing meal counts with a matching key exists, so this is a new meal count
        // Set the delivery date time to now
        for (const meal of this.meals) {
          meal.mealCounts.receivedMeals.mealDeliveryDateTime = mcDate.utcNow();
        }
      }

      let meal = this.meals.find(
        (m) => mcCounts.matchMealCountKey(m.mealCounts, mealKey)
      ) as MealCountDtoViewModel;

      let updatedCounts = new MealCountDtoViewModel(clone(meal?.mealCounts));

      this.pristine.push(updatedCounts);

      // If there are tempCounts with a matching key, then set the displayed meal counts to those values (after setting the pristine value!)
      if (tempCounts && mcCounts.matchMealCountKey(tempCounts, mealKey)) {
        meal.mealCounts = tempCounts;
      }

      if(meal.providedDays > 0)
        this.selectedProvidedDays = meal.providedDays;
    }

    this.validate();
  }

  /**
   * If tempCounts exist and match this key, then update the temp dto with the server-known values we got, except for the current user
   * @param tempCounts The temp counts to update
   * @param currentUser The current user
   * @param existingMealCountsWithMatchingKey Server-known meal counts stored in localstorage for this meal count
   */
  private updateTempDtoForOtherUsers(
    tempCounts: MealCountDto,
    currentUser: UserInfo,
    existingMealCountsWithMatchingKey: MealCountDto
  ) {
    if (
      tempCounts &&
      existingMealCountsWithMatchingKey &&
      mcCounts.matchMealCountKey(tempCounts, this.key)
    ) {
      // Add or update all tempCounts except for our current user's with the server-known data
      existingMealCountsWithMatchingKey.servingLines.forEach(
        (existingServingLine) => {
          let tempCountServingLineIndexToUpdate =
            tempCounts.servingLines.findIndex(
              (tempServingLine) =>
                tempServingLine.trackingId === existingServingLine.trackingId
            );

          if (
            tempCountServingLineIndexToUpdate > -1 &&
            tempCounts.servingLines[tempCountServingLineIndexToUpdate]
              ?.servedBy !== currentUser.userId
          ) {
            // If we found a matching temp serving line, update it with the server results
            tempCounts.servingLines[tempCountServingLineIndexToUpdate] =
              existingServingLine;
          } else if (tempCountServingLineIndexToUpdate === -1) {
            // If no matching serving line was found in the temp counts, add it
            tempCounts.servingLines.push(existingServingLine);
          }
        }
      );
    }
  }

  private updateMealCountsWithExistingMealCounts(
    existingMealCountsWithMatchingKey: MealCountDto,
    currentUser: UserInfo
  ) {
    let clonedMealCounts = clone<MealCountDto>(
      existingMealCountsWithMatchingKey
    );
    let mslInfo: FeatureFlagInfo = this.$store.getters[
      types.state.featureFlags
    ].find(
      (featureFlagInfo: FeatureFlagInfo) =>
        featureFlagInfo.siteId == clonedMealCounts.siteId
    );

    /*
     * If MSL active, create a new serving line for the current user if they don't already have one.
     * Otherwise, if it's SSL mode, set the current serving line Served By info to the current user
     */
    if (mslInfo.msl.isActive || clonedMealCounts.servingLines.length > 1) {
      this.createNewServingLineIfUserDoesntExistYet(
        clonedMealCounts,
        currentUser
      );
    } else {
      this.setFirstServingLineServedByInfoToCurrentUser(
        clonedMealCounts,
        currentUser
      );
    }
    const mealCounts = this.meals.find(
      (m) => mcCounts.matchMealCountKey(m.mealCounts, existingMealCountsWithMatchingKey)
    ) as MealCountDtoViewModel;
    mealCounts.mealCounts = clonedMealCounts;
  }

  private setFirstServingLineServedByInfoToCurrentUser(//TODO determine why this method exists
    clonedMealCounts: MealCountDto,
    currentUser: UserInfo
  ) {
    clonedMealCounts.servingLines[0].servedBy = currentUser.userId;
    clonedMealCounts.servingLines[0].servedByFirstName = currentUser.firstName;
    clonedMealCounts.servingLines[0].servedByLastName = currentUser.lastName;
  }

  private createNewServingLineIfUserDoesntExistYet(
    clonedMealCounts: MealCountDto,
    currentUser: UserInfo
  ) {
    let currentUsersClonedServingLine = mcCounts.findServingLine(
      clonedMealCounts.servingLines,
      currentUser.userId
    );
    if (!currentUsersClonedServingLine) {
      let newLine = mcCounts.getNewServingLine(currentUser);
      if (!clonedMealCounts.servingLines) {
        clonedMealCounts.servingLines = [];
      }
      clonedMealCounts.servingLines.push(newLine);
    }
  }

  validate() {
    //this.softValidations = this.softValidator.validate(this.model);
    //this.hardValidations = this.hardValidator.validate(this.mealCounts);    
    this.setIsDirty();
    eventBus.$emit(mcEvents.tallyNotify);
  }

  displayPossibleValidationServeDay() {
    if (ObjectHelper.keyExists(this.hardValidations, "mealDeliveryDateTime")) {
      this.$bvToast.show("dateWarning");
    }
  }

  displayPossibleValidationCounts() {
    if (ObjectHelper.keyExists(this.hardValidations, "servingLines")) {
      this.$bvToast.show("countsWarning");
    }
  }

  get isCountsValid() {
    return (
      !ObjectHelper.keyExists(this.hardValidations, "servingLines") &&
      !ObjectHelper.keyExists(this.hardValidations, "receivedMeals")
    );
  }

  changeTab(oldIndex: number, newIndex: number) {
    this.validate();
    if (oldIndex < newIndex) {
      this.displayPossibleValidationCounts();
      this.displayPossibleValidationServeDay();
    }
  }

  async inputChanged(mealTypeId: string) {
    this.setIsDirty();
    const model = this.meals.find(
      (m) => m.mealCounts.mealTypeId === mealTypeId
    );

    let userServingLine = model?.userServingLine;
    if (userServingLine) {
      // when set back to un-dirty state, if pristine was affirmed, reset affirmed to true
      if (!this.isDirty && userServingLine.affirmed === true) {
        userServingLine.affirmed = true;
      } else {
        userServingLine.affirmed = false;
      }
    }

    this.validate();
    this.saved = false;
    this.countComparison();
    this.$store.dispatch(types.actions.setTempDto, model?.mealCounts);


    //this.meals = [...this.meals];
  }

  affirmChanged() {
    for (const meal of this.meals) {
      meal.mealCounts.versionDateTimeUtc = moment.utc().format();
      const userServingLine = meal.userServingLine;
      // setting affirmed to false without model changes should not enable save.
      if (userServingLine && userServingLine.affirmed === true) {
        this.validate();
        this.saved = false;
      }
    }
  }

  get startIndex(): number {
    if (this.routeInfo.type === "serve") {
      return 1;
    }
    return 0;
  }

  cancel() {
    // Clear tempCounts
    this.clearTempCounts();

    this.$router.push(routePaths.dashboard.path);
  }

  async save() {
    const original = this.pristine;
    this.pristine = [];

    for (const meal of this.meals) {
      let isDirty = false;
      meal.mealCounts.editDateUtc = new Date();

      const pristineMeal = original.find((m) =>
        mcCounts.matchMealCountKey(m.mealCounts, meal.mealCounts)
      ) as MealCountDtoViewModel;

      const dirtyFlag = MealCountCheck.isDirty(pristineMeal, meal);

      if (dirtyFlag) {
        await this.$store.dispatch(types.actions.updateCountDto, meal.mealCounts);
        await this.$store.dispatch(
          types.actions.updateRequestToSyncUp,
          this.saveRequest(meal));

        // this should be handled by an event listener
        this.saved = true;
        this.$emit("savedValue", this.saved);

        //this.pristine.push(new MealCountDtoViewModel(clone(meal.mealCounts)));
        pristineMeal.mealCounts = clone(meal.mealCounts);
      }
    }

    this.setIsDirty();
    await this.$store.dispatch(
      types.actions.setTimeStamp,
      this.defaultTimeStamp
    );

    // Clear tempCounts
    this.clearTempCounts();

    // tell mc-sync we have new data
    eventBus.$emit(mcEvents.itemSaved);
  }

  saveRequest(meal: MealCountDtoViewModel) {
    const saveRequest = new SaveMealCountRequest(
      new MealCountVersion(
        meal.mealCounts.trackingId,
        meal.mealCounts.siteId,
        meal.mealCounts.programId,
        meal.mealCounts.mealTypeId,
        meal.mealCounts.mealServiceDate,
        meal.mealCounts.versionDateTimeUtc,
        meal.mealCounts.isNonCongregate as boolean,
        meal.providedDays,
      ),
      meal.mealCounts.receivedMeals,
      meal.userServingLine
    );

    return saveRequest;
  }

  get serveIncrement() {
    return this.multiplyMealCounts ? this.selectedProvidedDays : 1;
  }

  get readyToSubmit() {
    /*
     * cases:
     * changed data (isDirty = true) and set affirmed (affirm must be dirty)
     * didn't change data and set affirmed (affirm must be dirty)
     */
    const meal = this.meals.filter((m) => m.affirmed === true)[0];
    const pristineMeal = this.findPristineFromMealKey(this.pristine, meal);

    const result =
      this.isCountsValid && meal?.affirmed === true &&
       (this.isDirty || MealCountCheck.isAffirmDirty(pristineMeal?.userServingLine, meal.userServingLine));

    return result;
  }

  async submit() {
    await this.save();
    this.clearTempCounts();
    this.$router.push(routePaths.dashboard.path);
  }

  /**
   * Clear MealCounter.meals.tempCounts in local storage
   * @see Ticket: {@link https://colyar.atlassian.net/browse/MC-1228|MC-1228}
   * @see Documentation: {@link https://colyar.atlassian.net/wiki/spaces/DA/pages/561709094/Counting+App+recover+counts|Documentation}
   */
  clearTempCounts() {
    this.$store.dispatch(types.actions.setTempDto, {});
  }

  setIsDirty() {
    let isDirty = false;
    
    for (const meal of this.meals) {
      const pristine = this.findPristineFromMealKey(this.pristine ?? [], meal);
      const isDirtyFlag = MealCountCheck.isDirty(pristine, meal);
      
      if(isDirtyFlag) {
        isDirty = true;
        break;
      }
    }

    this.isDirty = isDirty;
    this.$emit("isDirty", this.isDirty);
  }

  findPristineFromMealKey(cleanMeals: MealCountDtoViewModel[], target: MealCountDtoViewModel) : MealCountDtoViewModel {
      return cleanMeals?.find(cleanMeal => 
        cleanMeal?.mealCounts?.siteId === target?.mealCounts?.siteId &&
        cleanMeal?.mealCounts?.programId === target?.mealCounts?.programId &&
        cleanMeal?.mealCounts?.mealTypeId?.toUpperCase() === target?.mealCounts?.mealTypeId?.toUpperCase() &&
        mcDate.toShortDate(cleanMeal?.mealCounts?.mealServiceDate) === mcDate.toShortDate(target?.mealCounts?.mealServiceDate)
      ) as MealCountDtoViewModel;
  }

  providedDaysSelected(number) {
    this.meals.forEach((meal) => {
      meal.mealCounts.providedDays = number;
    });
  }
}
